import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { Warning, AttachMoney } from "@mui/icons-material";
import { CommonDialog } from "@/components/common/CommonDialog";
import { FormButtonGroup } from "@/components/form";
import { useState } from "react";
import { toast } from "react-toastify";
import { PrimaryButton, SecondaryButton } from "@/components/common";
import { marketplaceService } from "@/services/marketplace/MarketplaceService";
import { useSessionContext } from "@/contexts/SessionContext";
import { buildCobrancaConsultaNotification } from "@/services/notification/notificationHelpers";
import { notificationService } from "@/services/notification/PostNotification";
import { EditAppointment, GetPatient } from "@/services/fhir";


interface IWarningAppointmentProps {
  isOpen: boolean;
  handleClose: () => void;
  onConfirm: () => void;
  appointmentDescription?: string;
  appointment?: fhir5.Appointment;
  hasSubscriptionFluxPay?: boolean;
  onPaymentSent?: () => void;
  setRefetch?: React.Dispatch<React.SetStateAction<boolean>>;
  fetchAppointments?: () => Promise<void>;
}

/**
 * Componente de diálogo que avisa o usuário quando está tentando iniciar um atendimento não pago
 * Exibe uma mensagem e oferece opções para continuar ou cancelar
 * Se hasSubscriptionFluxPay for true e o appointment tiver status "pending", exibe um botão para enviar cobrança
 */
export function WarningAppointment({
  isOpen,
  handleClose,
  onConfirm,
  appointmentDescription = "esta consulta",
  appointment,
  hasSubscriptionFluxPay = false,
  onPaymentSent,
  setRefetch,
  fetchAppointments,
}: IWarningAppointmentProps) {
  const { access } = useSessionContext();
  const [sendingPayment, setSendingPayment] = useState(false);
  
  // Verifica se o appointment tem status pending
  const isPending = appointment?.status === "pending";
  
  // Função para enviar cobrança
  const handleSendCharge = async () => {
    setSendingPayment(true);
    try {
      // Encontra o participant que é o paciente
      const patientParticipant = appointment?.participant?.find(
        (p) => p.actor?.reference?.startsWith('Patient/')
      );
      
      // Encontra o slot no appointment
      const slotReference = appointment?.slot?.[0]?.reference;
      
      // Encontra o practitioner no appointment
      const practitionerParticipant = appointment?.participant?.find(
        (p) => p.actor?.reference?.startsWith('Practitioner/')
      );

      // Encontra o healthcare service no appointment
      const healthcareParticipant = appointment?.participant?.find(
        (p) => p.actor?.reference?.startsWith('HealthcareService/')
      );
      
      if (!patientParticipant?.actor?.reference || !slotReference) {
        throw new Error("Dados insuficientes para enviar cobrança");
      }
      
      // Extrair informações necessárias
      const practitionerName = practitionerParticipant?.actor?.display || "";
      const healthcareDisplay = healthcareParticipant?.actor?.display || "";
      const patientCpf = patientParticipant.actor.reference.split("/")[1]?.split("-")[1] || "";
      
      // Buscar os dados completos do paciente para obter o nome
      let patientName = "";
      try {
        const patientResponse = await GetPatient({ identifier: patientCpf });
        patientName = patientResponse?.data?.name?.[0]?.text || "";
      } catch (error) {
        console.error("Erro ao buscar dados do paciente:", error);
        // Fallback para o display do actor se não conseguir obter o nome do paciente
        patientName = patientParticipant.actor.display || "";
      }
      
      // Obter o link de pagamento
      const paymentLink = await marketplaceService.sendPaymentMessage({
        slotReference,
        patientName,
        healthcareDisplay,
        practitionerName,
        phoneNumber: "", // O número de telefone será obtido do registro do paciente pelo serviço
      });
      
      // Enviar notificação com o template de pagamento
      await notificationService.sendNotification(
        buildCobrancaConsultaNotification({
          cpf: patientCpf,
          nomePaciente: patientName,
          tipoConsulta: healthcareDisplay,
          nomeProfissional: practitionerName,
          linkPagamento: paymentLink,
          organizationId: access.organizationId,
        }),
        { organizationId: access.organizationId },
      );
      
      // Atualizar appointment com extension notification-sent
      const notificationSentExtension = appointment?.extension?.find(
        (ext: any) => ext.url === "https://fluxmed.com.br/fhir/StructureDefinition/notification-sent"
      );
      
      if (!notificationSentExtension?.valueBoolean) {
        await EditAppointment({
          ...appointment,
          extension: [
            ...(appointment?.extension || []),
            {
              url: "https://fluxmed.com.br/fhir/StructureDefinition/notification-sent",
              valueBoolean: true,
            },
          ],
        });
      }
      
      toast.success("Cobrança enviada com sucesso!");
      
      // Atualiza a lista de appointments usando a função fetchAppointments (se disponível)
      // ou setRefetch como fallback
      if (fetchAppointments) {
        await fetchAppointments();
      } else if (setRefetch) {
        setRefetch(prev => !prev);
      }
      
      // Notifica que o pagamento foi enviado
      if (onPaymentSent) {
        onPaymentSent();
      }
      
      // Fecha o diálogo
      handleClose();
    } catch (error: any) {
      console.error("Erro ao enviar cobrança:", error);
      
      if (error?.response?.status === 404) {
        toast.error("Não é possível enviar cobrança para consulta em agenda sem custo.");
      } else {
        toast.error("Não foi possível enviar a cobrança. Tente novamente.");
      }
    } finally {
      setSendingPayment(false);
    }
  };

  return (
    <CommonDialog isOpen={isOpen} handleClose={handleClose}>
      <DialogTitle sx={{ textAlign: "center", paddingTop: 2 }}>
        <Warning sx={{ color: "warning.main", fontSize: 48 }} />
        <Typography variant="h6" fontWeight="bold" mt={1}>
          Atendimento Não Pago
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Typography align="center">
            Atenção! {appointmentDescription} não possui pagamento registrado no sistema.
            {hasSubscriptionFluxPay && isPending ? 
              " É necessário enviar uma cobrança para prosseguir." : 
              " Deseja prosseguir com o atendimento mesmo assim?"}
          </Typography>
          
          {hasSubscriptionFluxPay && isPending ? (
            <Stack
              width="100%"
              direction={{ xs: "column", md: "row-reverse" }}
              spacing={{ xs: 2, lg: 4 }}
              justifyContent="space-between"
            >
              <PrimaryButton
                width="100%"
                height="50px"
                loading={sendingPayment}
                onClick={handleSendCharge}
                startIcon={!sendingPayment && <AttachMoney />}
              >
                {sendingPayment ? "Enviando..." : "Enviar Cobrança"}
              </PrimaryButton>

              <SecondaryButton
                width="100%"
                height="50px"
                onClick={handleClose}
                id="goBackButton"
              >
                Cancelar
              </SecondaryButton>
            </Stack>
          ) : (
            <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
              <FormButtonGroup 
                onGoBackButtonClick={handleClose}
                onNextButtonClick={onConfirm}
                goBackButtonText="Cancelar"
                nextButtonText="Prosseguir"
              />
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </CommonDialog>
  );
}